// MF-67 — Renders one template field (by type) as a labelled, controlled input: title on top, the
// field's instructions (subtitle) under it. Shared by the entry form and the template form's live
// preview (`preview` disables uploads there), so staff see exactly what the template builder shows.
// Values: text/textarea/date/time/dropdown/radio → string · number → string of digits ·
// checkbox → boolean · list/grouped-checkbox → string[] · image/attachment → [{ id, name, url, type, size }].
import {
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@mui/material'
import FormField from './FormField'
import NumberField from './NumberField'
import FileUpload from './FileUpload'
import { inputSx } from './formStyles'

export const FIELD_TYPES = [
  { v: 'text', label: 'Short text' },
  { v: 'textarea', label: 'Long text' },
  { v: 'number', label: 'Number' },
  { v: 'date', label: 'Date' },
  { v: 'time', label: 'Time' },
  { v: 'checkbox', label: 'Yes / No (checkbox)' },
  { v: 'radio', label: 'Single choice (buttons)' },
  { v: 'dropdown', label: 'Dropdown (pick one)' },
  { v: 'list', label: 'Dropdown (pick several)' },
  { v: 'grouped-checkbox', label: 'Multiple choice (checkboxes)' },
  { v: 'image', label: 'Image upload' },
  { v: 'attachment', label: 'File attachment' }
]
// Types whose answers come from a list of options defined in the template.
export const HAS_OPTIONS = ['radio', 'dropdown', 'list', 'grouped-checkbox']
const MULTI = ['list', 'grouped-checkbox', 'image', 'attachment']

export const fieldTypeLabel = (t) => FIELD_TYPES.find((x) => x.v === t)?.label || t

const optValue = (o) => o?.value ?? o
const optLabel = (o) => o?.label ?? o

// Is a field's answer missing? (required-field check)
export const isFieldEmpty = (field, value) => {
  if (field.type === 'checkbox') return value !== true
  if (MULTI.includes(field.type)) return !Array.isArray(value) || value.length === 0
  return value === undefined || value === null || String(value).trim() === ''
}

const Control = ({ field, value, onChange, id, preview }) => {
  const options = field.options || []
  const common = {
    id,
    fullWidth: true,
    size: 'small',
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    sx: inputSx
  }
  switch (field.type) {
    case 'textarea':
      return <TextField {...common} multiline minRows={3} />
    case 'number':
      return <NumberField {...common} allowDecimal />
    case 'date':
      return <TextField {...common} type='date' />
    case 'time':
      return <TextField {...common} type='time' />
    case 'dropdown':
      return (
        <TextField {...common} select SelectProps={{ displayEmpty: true, labelId: `${id}-label` }}>
          <MenuItem value=''>
            <em style={{ color: 'rgb(148, 163, 184)' }}>Select…</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={optValue(opt)} value={optValue(opt)}>
              {optLabel(opt)}
            </MenuItem>
          ))}
        </TextField>
      )
    case 'list': {
      const selected = Array.isArray(value) ? value : []
      return (
        <Select
          id={id}
          multiple
          displayEmpty
          fullWidth
          size='small'
          labelId={`${id}-label`}
          value={selected}
          onChange={(e) =>
            onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
          }
          sx={{ backgroundColor: '#fff', borderRadius: '8px' }}
          renderValue={(vals) =>
            vals.length ? (
              <div className='flex flex-wrap' style={{ gap: 4 }}>
                {vals.map((v) => (
                  <Chip key={v} size='small' label={v} />
                ))}
              </div>
            ) : (
              <em style={{ color: 'rgb(148, 163, 184)' }}>Select one or more…</em>
            )
          }
        >
          {options.map((opt) => (
            <MenuItem key={optValue(opt)} value={optValue(opt)}>
              <Checkbox size='small' checked={selected.includes(optValue(opt))} />
              {optLabel(opt)}
            </MenuItem>
          ))}
        </Select>
      )
    }
    case 'radio':
      return (
        <RadioGroup
          row
          aria-labelledby={`${id}-label`}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ gap: '0 16px' }}
        >
          {options.map((opt) => (
            <FormControlLabel
              key={optValue(opt)}
              value={optValue(opt)}
              control={<Radio size='small' />}
              label={<span style={{ fontSize: 13 }}>{optLabel(opt)}</span>}
            />
          ))}
        </RadioGroup>
      )
    case 'grouped-checkbox': {
      const selected = Array.isArray(value) ? value : []
      return (
        <div
          className='flex flex-wrap'
          role='group'
          aria-labelledby={`${id}-label`}
          style={{ gap: '0 16px' }}
        >
          {options.map((opt) => {
            const v = optValue(opt)
            const on = selected.includes(v)
            return (
              <FormControlLabel
                key={v}
                control={
                  <Checkbox
                    size='small'
                    checked={on}
                    onChange={() => onChange(on ? selected.filter((x) => x !== v) : [...selected, v])}
                  />
                }
                label={<span style={{ fontSize: 13 }}>{optLabel(opt)}</span>}
              />
            )
          })}
        </div>
      )
    }
    case 'image':
      return <FileUpload kind='image' value={value} onChange={onChange} disabled={preview} />
    case 'attachment':
      return <FileUpload kind='file' value={value} onChange={onChange} disabled={preview} />
    default:
      return <TextField {...common} />
  }
}

const FieldInput = ({ field, value, onChange, preview = false }) => {
  const id = `journal-field-${field.id}`
  const noOptions = HAS_OPTIONS.includes(field.type) && !(field.options || []).length
  if (field.type === 'checkbox') {
    return (
      <FormField
        label={field.label || 'Untitled field'}
        required={field.required}
        subtitle={field.description}
      >
        <FormControlLabel
          control={
            <Checkbox size='small' checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          }
          label={<span style={{ fontSize: 13 }}>Yes</span>}
        />
      </FormField>
    )
  }
  return (
    <FormField
      label={field.label || 'Untitled field'}
      required={field.required}
      subtitle={field.description}
      hint={noOptions ? 'Add options to this field in the template.' : ''}
      htmlFor={id}
    >
      <Control field={field} value={value} onChange={onChange} id={id} preview={preview} />
    </FormField>
  )
}

export default FieldInput
